import {
  ENTITY_OWNER_DOCUMENT_TYPES,
  KYB_BUSINESS_DOCUMENT_LOOKUP_KEYS,
  KYB_COMPANY_ADDRESS_LOOKUPS,
  KYB_COMPANY_DETAILS_LOOKUPS,
  KYB_COMPANY_OWNER_LOOKUP_KEYS,
  KYB_DOCUMENT_INDEX_PREFIX,
  KYB_DOCUMENT_LABELS,
  KYB_DOCUMENT_OWNER_LABELS,
  KYB_FIELD_LABEL_OVERRIDES,
  KYB_FIELD_LABELS,
  KYB_NOT_AVAILABLE,
  KYB_PARTNER_DOCUMENT_TYPE_LABELS,
  KYB_SECTION_FIELD_ORDER,
  KYB_SECTION_LABELS,
  KYB_SECTION_ORDER,
  KYB_SIGNED_DOCUMENT_KEYS,
  KYB_VALUE_LABELS,
  ONE_MONEY_DOCUMENT_KEY_PREFIX,
  STANDARD_DOCUMENT_KEY_PREFIX,
} from 'modules/merchantinfo/constants/constants';
import { snakeToTitleCase } from 'utils/utils';

const OTHER_SECTION_KEY = 'other';
const SOURCE_OF_FUNDS_PARENT = 'source_of_funds';
const SOURCE_OF_WEALTH_PARENT = 'source_of_wealth';
const SOURCE_OF_FUNDS_AND_WEALTH_SECTION = 'source_of_funds_and_wealth';

const BRACKET_INDEX_REGEX = /^(.*)\[(\d+)\]$/;
const UNDERSCORE_INDEX_REGEX = /^(.*)_(\d+)$/;

const CLOUD_PATH_REGEX = /^[\w][\w.-]*\/\S+\.[A-Za-z0-9]{2,6}$/;

const HTTP_URL_REGEX = /^https?:\/\//;

const OWNER_KEY_REGEX = /^(company|controller|intermediary)(_\d+)?$/;

export const isKybHttpUrl = (value) => typeof value === 'string' && HTTP_URL_REGEX.test(value);

export const isKybCloudStoragePath = (value) => typeof value === 'string' && CLOUD_PATH_REGEX.test(value.trim());

export const getKybFileName = (value) => (typeof value === 'string' ? value.split('/').pop() : '');

export const isKybDocumentKey = (key) => {
  if (!key) return false;

  return (
    Object.prototype.hasOwnProperty.call(KYB_DOCUMENT_LABELS, key) ||
    key.includes(KYB_DOCUMENT_INDEX_PREFIX) ||
    key.startsWith(ONE_MONEY_DOCUMENT_KEY_PREFIX) ||
    key.startsWith(STANDARD_DOCUMENT_KEY_PREFIX)
  );
};

export const isKybSignedDocumentKey = (key) =>
  Boolean(key) && (KYB_SIGNED_DOCUMENT_KEYS.includes(key) || key.includes(KYB_DOCUMENT_INDEX_PREFIX));

const parseJson = (value) => {
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();

  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;

  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
};

export const parseKybDocumentPayload = (value) => {
  const parsed = parseJson(value);

  if (!parsed || Array.isArray(parsed) || !parsed.cloud_identifier) return null;

  return {
    cloudIdentifier: parsed.cloud_identifier,
    fileName: parsed.file_name || getKybFileName(parsed.cloud_identifier),
    documentType: parsed.document_type || '',
    partnerDocumentType: parsed.partner_document_type || '',
    owner: parsed.owner || '',
    contentType: parsed.content_type || '',
    issuedDate: parsed.issued_date || '',
    expiryDate: parsed.expiry_date || '',
  };
};

export const getKybDocumentOwnerLabel = (owner) => {
  if (!owner) return '';

  const match = owner.match(UNDERSCORE_INDEX_REGEX);
  const base = match ? match[1] : owner;
  const label = lookupLabel(KYB_DOCUMENT_OWNER_LABELS, base) || snakeToTitleCase(base);

  return match ? `${label} ${Number(match[2]) + 1}` : label;
};

const lookupLabel = (map, key) => (Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined);

export const getKybPartnerDocumentTypeLabel = (documentType) =>
  lookupLabel(KYB_PARTNER_DOCUMENT_TYPE_LABELS, documentType) ||
  (documentType ? snakeToTitleCase(documentType.toLowerCase()) : '');

export const splitKybParent = (parent) => {
  if (!parent || parent === 'null') return { base: OTHER_SECTION_KEY, index: null };

  const bracketMatch = parent.match(BRACKET_INDEX_REGEX);

  if (bracketMatch) return { base: bracketMatch[1], index: Number(bracketMatch[2]) };

  const underscoreMatch = parent.match(UNDERSCORE_INDEX_REGEX);

  if (underscoreMatch && Object.prototype.hasOwnProperty.call(KYB_SECTION_LABELS, underscoreMatch[1])) {
    return { base: underscoreMatch[1], index: Number(underscoreMatch[2]) };
  }

  return { base: parent, index: null };
};

export const getKybSectionLabel = (base, index) => {
  const label = lookupLabel(KYB_SECTION_LABELS, base) || snakeToTitleCase(base);

  return index === null || index === undefined ? label : `${label} ${index + 1}`;
};

export const getKybFieldLabel = (key, parentBase) => {
  const override = lookupLabel(KYB_FIELD_LABEL_OVERRIDES, parentBase)?.[key];

  if (override) return override;

  return lookupLabel(KYB_DOCUMENT_LABELS, key) || lookupLabel(KYB_FIELD_LABELS, key) || snakeToTitleCase(key);
};

export const formatKybBoolean = (value) => {
  if (value === 'true' || value === true) return 'Yes';
  if (value === 'false' || value === false) return 'No';

  return value;
};

export const formatKybScalarValue = (value) => {
  if (value === null || value === undefined || value === '') return KYB_NOT_AVAILABLE;

  const parsed = parseJson(value);

  if (Array.isArray(parsed)) {
    return parsed.length ? parsed.join(', ') : KYB_NOT_AVAILABLE;
  }

  if (parsed && typeof parsed === 'object') {
    if (parsed.phone || parsed.dial_code) {
      return [parsed.dial_code, parsed.phone].filter(Boolean).join(' ') || KYB_NOT_AVAILABLE;
    }

    const entries = Object.entries(parsed).filter(([, entryValue]) => entryValue !== '' && entryValue !== null);

    return entries.length
      ? entries
          .map(([entryKey, entryValue]) => {
            if (OWNER_KEY_REGEX.test(entryKey)) return `${getKybDocumentOwnerLabel(entryKey)}: ${entryValue}`;

            const label = /^[a-z0-9_]+$/.test(entryKey) ? snakeToTitleCase(entryKey) : entryKey;

            return `${label}: ${entryValue}`;
          })
          .join(', ')
      : KYB_NOT_AVAILABLE;
  }

  const asString = String(value);

  return lookupLabel(KYB_VALUE_LABELS, asString) || formatKybBoolean(asString);
};

const buildDocumentRow = (detail, keyName) => {
  const { value } = detail;
  const payload = parseKybDocumentPayload(value);

  if (payload) {
    const ownerLabel = getKybDocumentOwnerLabel(payload.owner);
    const typeLabel = getKybPartnerDocumentTypeLabel(payload.documentType);
    const helperParts = [`Storage path: ${payload.cloudIdentifier}`];

    if (payload.partnerDocumentType && payload.partnerDocumentType !== payload.documentType) {
      helperParts.unshift(`Sent to partner as: ${payload.partnerDocumentType}`);
    }
    if (payload.issuedDate) helperParts.unshift(`Issued: ${payload.issuedDate}`);
    if (payload.expiryDate) helperParts.unshift(`Expires: ${payload.expiryDate}`);

    return {
      keyName: [typeLabel || keyName, ownerLabel && `(${ownerLabel})`].filter(Boolean).join(' '),
      value: payload.fileName,
      helperText: helperParts.join(' · '),
    };
  }

  if (isKybHttpUrl(value)) {
    return {
      keyName,
      value,
      linkText: `Open ${getKybFileName(value.split('?')[0])}`,
      helperText: 'Signed download link - it expires a few minutes after this page was loaded.',
    };
  }

  if (isKybCloudStoragePath(value)) {
    const reason = isKybSignedDocumentKey(detail?.key)
      ? 'the API could not sign it'
      : 'the API does not sign this document type';

    return {
      keyName,
      value: getKybFileName(value),
      helperText: `Storage path: ${value} - no download link because ${reason}`,
    };
  }

  return { keyName, value: formatKybScalarValue(value) };
};

export const buildKybDetailRow = (detail, parentBase) => {
  const key = detail?.key;
  const label = getKybFieldLabel(key, parentBase);
  const row = isKybDocumentKey(key)
    ? buildDocumentRow(detail, label)
    : { keyName: label, value: formatKybScalarValue(detail?.value) };

  if (detail?.message && detail.message !== key && !row.helperText) {
    row.helperText = detail.message;
  }

  return { id: detail?.id || `${key}_${detail?.parent}`, ...row };
};

const hasValue = (kybMap, compositeKey) => {
  const value = kybMap?.[compositeKey]?.value;

  return value !== undefined && value !== null && value !== '';
};

const addResolvedKey = (renderedKeys, kybMap, lookupChain) => {
  const resolvedKey = lookupChain.find((compositeKey) => hasValue(kybMap, compositeKey));

  if (resolvedKey) renderedKeys.add(resolvedKey);
};

export const buildKybLegacyRenderedKeys = (
  kybMap,
  { numOfCompanyOwners = 0, additionalBusinessDocsCount = 0 } = {}
) => {
  const renderedKeys = new Set();

  Object.values(KYB_COMPANY_DETAILS_LOOKUPS).forEach((chain) => addResolvedKey(renderedKeys, kybMap, chain));
  Object.values(KYB_COMPANY_ADDRESS_LOOKUPS).forEach((chain) => addResolvedKey(renderedKeys, kybMap, chain));
  KYB_BUSINESS_DOCUMENT_LOOKUP_KEYS.forEach((key) =>
    addResolvedKey(renderedKeys, kybMap, [`${key}_business_document`])
  );
  ENTITY_OWNER_DOCUMENT_TYPES.forEach((documentType) =>
    addResolvedKey(renderedKeys, kybMap, [`${documentType?.value}_business_document`])
  );

  for (let index = 0; index < numOfCompanyOwners; ++index) {
    KYB_COMPANY_OWNER_LOOKUP_KEYS.forEach((key) =>
      addResolvedKey(renderedKeys, kybMap, [`${key}_company_owners[${index}]`])
    );
    ENTITY_OWNER_DOCUMENT_TYPES.forEach((documentType) =>
      addResolvedKey(renderedKeys, kybMap, [`${documentType?.value}_company_owners[${index}]`])
    );
  }

  for (let index = 0; index < additionalBusinessDocsCount; ++index) {
    addResolvedKey(renderedKeys, kybMap, [`document[${index}]_additional_business_document`]);
  }

  return renderedKeys;
};

const getFieldSortValue = (base, key, isDocument) => {
  const order = KYB_SECTION_FIELD_ORDER[base] || [];
  const position = order.indexOf(key);

  if (position !== -1) return [0, position, ''];

  return [isDocument ? 2 : 1, 0, key];
};

const compareRows = (a, b) =>
  a.sortValue[0] - b.sortValue[0] || a.sortValue[1] - b.sortValue[1] || a.sortValue[2].localeCompare(b.sortValue[2]);

const compareSections = (a, b) => {
  const aOrder = KYB_SECTION_ORDER.indexOf(a.parentBase);
  const bOrder = KYB_SECTION_ORDER.indexOf(b.parentBase);
  const aPosition = aOrder === -1 ? KYB_SECTION_ORDER.length : aOrder;
  const bPosition = bOrder === -1 ? KYB_SECTION_ORDER.length : bOrder;

  return aPosition - bPosition || a.parentBase.localeCompare(b.parentBase) || (a.index ?? -1) - (b.index ?? -1);
};

const buildSourceOfFundsSection = (optionGroups) => {
  const rows = [];

  [SOURCE_OF_FUNDS_PARENT, SOURCE_OF_WEALTH_PARENT].forEach((base) => {
    const options = optionGroups[base];

    if (!options?.length) return;

    const values = options.filter(Boolean).map((option) => {
      const label = formatKybScalarValue(option.value);

      return option.isDisabled === 'true' ? `${label} (disabled)` : label;
    });

    rows.push({
      id: `${base}_selected_options`,
      keyName: getKybFieldLabel(base, SOURCE_OF_FUNDS_AND_WEALTH_SECTION),
      value: values.join(', '),
      sortValue: [0, base === SOURCE_OF_FUNDS_PARENT ? 0 : 1, ''],
    });
  });

  if (!rows.length) return null;

  return {
    parent: SOURCE_OF_FUNDS_AND_WEALTH_SECTION,
    parentBase: SOURCE_OF_FUNDS_AND_WEALTH_SECTION,
    index: null,
    label: getKybSectionLabel(SOURCE_OF_FUNDS_AND_WEALTH_SECTION, null),
    rows,
  };
};

export const buildKybDetailSections = (details, renderedKeys = new Set()) => {
  const sectionsByParent = {};
  const optionGroups = {};
  const skippedKeys = new Set();

  (details || []).forEach((detail) => {
    const key = detail?.key;

    if (!key || key === 'null') return;

    const parent = detail?.parent && detail.parent !== 'null' ? detail.parent : OTHER_SECTION_KEY;
    const compositeKey = `${key}_${parent}`;

    if (renderedKeys.has(compositeKey) && !skippedKeys.has(compositeKey)) {
      skippedKeys.add(compositeKey);

      return;
    }

    const { base, index } = splitKybParent(parent);

    if (base === SOURCE_OF_FUNDS_PARENT || base === SOURCE_OF_WEALTH_PARENT) {
      if (['value', 'label', 'isDisabled', 'icon'].includes(key)) {
        const groupIndex = index ?? 0;

        optionGroups[base] = optionGroups[base] || [];
        optionGroups[base][groupIndex] = { ...optionGroups[base][groupIndex], [key]: detail?.value };

        return;
      }
    }

    if (detail?.value === null || detail?.value === undefined || detail?.value === '') return;

    const row = buildKybDetailRow(detail, base);

    sectionsByParent[parent] = sectionsByParent[parent] || {
      parent,
      parentBase: base,
      index,
      label: getKybSectionLabel(base, index),
      rows: [],
    };
    sectionsByParent[parent].rows.push({ ...row, sortValue: getFieldSortValue(base, key, isKybDocumentKey(key)) });
  });

  const sourceSection = buildSourceOfFundsSection(optionGroups);
  const sections = Object.values(sectionsByParent);

  if (sourceSection) sections.push(sourceSection);

  sections.forEach((section) => section.rows.sort(compareRows));

  return sections.sort(compareSections);
};
